import React, { useEffect, useState, useMemo, useRef } from 'react';
import { getProfile, updateProfile, uploadImage } from '../api/authService';
import { compressImage } from '../utils/image';
import Avatar from '../components/atoms/Avatar';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/atoms/Toast';

const Profile = () => {
  const { token, refreshProfile } = useAuth();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', profileImage: '' });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [zoom, setZoom] = useState(1);
  const dropRef = useRef(null);

  const fullName = useMemo(() => `${form.firstName} ${form.lastName}`.trim(), [form.firstName, form.lastName]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await getProfile(token);
        if (mounted) {
          setData(res);
          setForm({ firstName: res.firstName || '', lastName: res.lastName || '', profileImage: res.profileImage || '' });
        }
      } catch (e) {
        if (mounted) setError(e.message || 'Failed to load profile');
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      let payload = { ...form };
      // If a new file is selected, upload it first
      if (file) {
        const small = await compressImage(file, { maxSize: 512, quality: 0.8 });
        const { absoluteUrl, url } = await uploadImage(small, token);
        payload.profileImage = absoluteUrl || url; // Prefer absolute for cross-origin
      }
      const updated = await updateProfile(payload, token);
      setData(updated);
      setForm({ firstName: updated.firstName || '', lastName: updated.lastName || '', profileImage: updated.profileImage || '' });
      setFile(null);
      setPreview('');
      toast?.success('Profile updated');
      // refresh global profile to update navbar/avatar
      await refreshProfile();
    } catch (e) {
      setError(e.message || 'Failed to save');
      toast?.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (error) return <div className="max-w-3xl mx-auto p-6 text-red-600">{error}</div>;
  if (!data) return <div className="max-w-3xl mx-auto p-6">Loading…</div>;

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const f = e.dataTransfer.files?.[0];
    if (f && f.type.startsWith('image/')) {
      setFile(f);
      setPreview(URL.createObjectURL(f));
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Left: Profile card */}
        <div className="md:col-span-1">
          <div className="rounded-2xl border bg-white shadow-sm p-6 sticky top-6">
            <div className="flex items-center gap-4">
              <Avatar src={form.profileImage} name={fullName} email={data.email} size={72} />
              <div>
                <h1 className="text-xl font-semibold">{fullName || 'Your Name'}</h1>
                <p className="text-gray-600 text-sm">{data.email}</p>
                <span className="inline-block mt-1 text-xs rounded bg-gray-100 text-gray-700 px-2 py-0.5">{data.role}</span>
              </div>
            </div>
            {form.profileImage && (
              <div className="mt-4 text-xs text-gray-500 break-all">Image: {form.profileImage}</div>
            )}
          </div>
        </div>

        {/* Right: Edit form */}
        <div className="md:col-span-2">
          <form onSubmit={onSave} className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold">Basic info</h2>
              <p className="text-sm text-gray-600">Update your personal details and profile picture.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">First name</label>
                <input className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" value={form.firstName} onChange={e => setForm(f => ({...f, firstName: e.target.value}))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Last name</label>
                <input className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" value={form.lastName} onChange={e => setForm(f => ({...f, lastName: e.target.value}))} />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Profile image URL</label>
              <input className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" value={form.profileImage} onChange={e => setForm(f => ({...f, profileImage: e.target.value}))} placeholder="https://..." />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Upload a new profile picture</label>
              <div
                ref={dropRef}
                onDrop={onDrop}
                onDragOver={onDragOver}
                className="border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center text-center text-gray-600 hover:border-purple-400 hover:bg-purple-50/30 transition"
              >
                <div className="flex items-center gap-4">
                  {preview ? (
                    <img src={preview} alt="Preview" className="h-24 w-24 rounded-full object-cover" style={{ transform: `scale(${zoom})` }} />
                  ) : (
                    <div className="h-24 w-24 rounded-full bg-gray-100 flex items-center justify-center text-3xl">🖼️</div>
                  )}
                  <div className="text-left">
                    <p className="font-medium">Drag & drop an image here</p>
                    <p className="text-sm text-gray-500">or click to select a file</p>
                    <div className="mt-2">
                      <label className="inline-block px-3 py-1.5 text-sm rounded bg-purple-600 text-white hover:bg-purple-700 cursor-pointer">
                        Choose file
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            setFile(f || null);
                            setPreview(f ? URL.createObjectURL(f) : '');
                          }}
                        />
                      </label>
                    </div>
                  </div>
                </div>
                {preview && (
                  <div className="w-full mt-4">
                    <label className="text-sm mr-2">Zoom</label>
                    <input type="range" min={1} max={2} step={0.01} value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))} />
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">PNG, JPG up to 5MB.</p>
            </div>

            <div className="flex items-center gap-3">
              <button disabled={saving} className="bg-purple-600 text-white px-4 py-2 rounded-lg disabled:opacity-50 shadow-sm hover:bg-purple-700">{saving ? 'Saving…' : 'Save changes'}</button>
              {error && <span className="text-sm text-red-600">{error}</span>}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;
